import capitalize from 'lodash/capitalize';

import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import Helper from '@wetransfer/transfer/helper';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { makeTransferEventData } from '@wetransfer/transfer/transfer-bundle/helpers/make-transfer-event-data';
import { NormalizedTransfer } from '@wetransfer/transfer/transfer-bundle/types/transfers';

import {
  trackAmplitudeEvent,
  setAmplitudeUserProperties,
} from './vendors/amplitude';
import { getBillingInterval } from './vendors/utils/trackDownload';

// event that fires for every send transfer
export function sendTransfer({
  transfer,
  isForwardedTransfer,
  currentSize,
}: {
  transfer: NormalizedTransfer;
  isForwardedTransfer: boolean;
  currentSize: number;
}): void {
  const transferEventData = makeTransferEventData({
    transfer,
    mode: 'upload',
    isForwardedTransfer,
    currentSize,
  });

  Analytics.trackGoogleEvent({
    category: 'Transfer',
    action: 'Upload',
    label: transfer.type,
    value: currentSize,
    transfer: transferEventData,
    user: {
      download_count: Helper.getWTDownloaded(),
      upload_count: Helper.getWTSent(),
      first_visit_date: Helper.getFirstVisitDate(),
    },
  });

  Analytics.trackWithPixelOrSDK(
    'WeTransfer_Site_Landing_Page_Overlay_WT_plus_send_file_Conversion'
  );
  Analytics.trackSnowplowEvent({
    category: 'transfer',
    action: 'click_transfer',
    label: `transfer-${
      transferEventData.password ? 'with' : 'without'
    }-password-expiryDate-${transfer.expiry}`,
  });
}

export function addFiles(): void {
  Analytics.trackGoogleEvent({
    category: 'Transfer',
    action: 'File Added',
    label: 'button',
  });
  Analytics.trackSnowplowEvent({
    category: 'transfer',
    action: 'click_add_file',
  });
}

interface ConfigChangeTrackingInput {
  expiryDate: string;
  transferType: string;
  hasPassword: boolean;
}

export function expiryDate({
  expiryDate,
  transferType,
  hasPassword,
}: ConfigChangeTrackingInput): void {
  Analytics.trackGoogleEvent({
    category: 'Transfer',
    action: 'Preferences - Change Expiry',
    label: `Type-${transferType} Password-${
      hasPassword ? 'yes' : 'no'
    } Expiry-${expiryDate}`,
  });
  Analytics.trackSnowplowEvent({
    category: 'transfer_window',
    action: 'click_dotdotdot',
    label: `expire_after_${expiryDate}`,
    property: 'pro_account',
  });
}

export function changeType({
  hasPassword,
  transferType,
  expiryDate,
}: ConfigChangeTrackingInput): void {
  Analytics.trackGoogleEvent({
    category: 'Transfer',
    action: 'Preferences - Switch Transfer Type',
    label: `Type-${transferType} Password-${
      hasPassword ? 'yes' : 'no'
    } Expiry-${expiryDate}`,
  });

  Analytics.trackSnowplowEvent({
    category: 'transfer',
    action: 'click_more_button',
    label: `send_as_${transferType}`,
  });
}

export function transferDone(): void {
  Analytics.trackSnowplowEvent({
    category: 'transfer',
    action: 'click_send_another',
  });
}

export function upsellClick(upsellKey: string): void {
  Analytics.trackSnowplowEvent({
    category: 'upsell',
    action: 'click_upsell_button',
    label: upsellKey,
    property: 'upload_page',
  });
}

export function upsellImpression(upsellKey: string): void {
  Analytics.trackSnowplowEvent({
    category: 'upsell',
    action: 'show_page',
    label: upsellKey,
    property: 'upload_page',
  });
}

export function upsellContinue(): void {
  Analytics.trackSnowplowEvent({
    category: 'transfer',
    action: 'click_continue',
  });
}

export type trackingTiers = 'free' | 'pro' | 'pro_2022' | 'premium_2022';

export function getTierForTracking(tier: string): trackingTiers {
  if (tier === PLAN_TIER.FREE) {
    return 'free';
  }

  if (tier === PLAN_TIER.PLUS) {
    return 'pro';
  }

  if (tier === PLAN_TIER.PRO_2022) {
    return 'pro_2022';
  }

  if (tier === PLAN_TIER.PREMIUM_2022) {
    return 'premium_2022';
  }
}

export function getAmplitudeSubscriptionTier(
  tier: string
): 'Pro' | 'Premium' | null {
  if (tier === PLAN_TIER.PLUS || tier === PLAN_TIER.PRO_2022) {
    return 'Pro';
  }

  if (tier === PLAN_TIER.PREMIUM_2022) {
    return 'Premium';
  }

  return null;
}

export function getAmplitudeSubscriptionPeriod(
  period: string
): 'Monthly' | 'Yearly' {
  if (period === 'monthly' || period === 'month') {
    return 'Monthly';
  }

  return 'Yearly';
}

export function getAmplitudePaymentMethod(
  method: string
):
  | 'PayPal'
  | 'iDeal'
  | 'Credit Card'
  | 'SOFORT'
  | 'Bancontact'
  | 'Apple Pay'
  | 'Google Pay' {
  if (method === 'paypal') {
    return 'PayPal';
  }

  if (method === 'ideal') {
    return 'iDeal';
  }

  if (method === 'card') {
    return 'Credit Card';
  }

  if (method === 'sofort') {
    return 'SOFORT';
  }

  if (method === 'bancontact') {
    return 'Bancontact';
  }

  if (method === 'apple_pay') {
    return 'Apple Pay';
  }

  if (method === 'google_pay') {
    return 'Google Pay';
  }
}

export function newSignup({
  userId,
  tier,
  interval,
  value,
  currency,
  transaction_id,
  coupon,
  tax,
  discount,
}: {
  userId: string;
  tier: string;
  interval: string;
  value: number;
  currency: string;
  transaction_id: string;
  coupon: string;
  tax: number;
  discount: number;
}): void {
  // Snowplow and GA have different schema for this data
  const spTier = getTierForTracking(tier);

  const gaTier = capitalize(tier);
  const referralKey = Analytics.getReferralKey() || '';

  // Returns:
  //   0 for free account
  //   1 for monthly
  //   12 for yearly
  function spSubscriptionLength(tier: string, interval: string): number {
    if (tier === PLAN_TIER.PLUS) {
      return interval === 'year' ? 12 : 1;
    }

    if (tier !== PLAN_TIER.FREE) {
      return interval === 'year' ? 12 : 1;
    }

    return 0;
  }
  const spSubscriptionLengthInMonths = spSubscriptionLength(tier, interval);

  // for GA, we pass 'year', 'month', or ''
  let gaSubscriptionLength;

  if (tier !== PLAN_TIER.FREE) {
    gaSubscriptionLength = interval;
  }

  Analytics.trackSnowplowEvent({
    user_id: userId,
    category: 'sign_up_page',
    action: 'sign_up_completed_successfully',
    label: referralKey,
    property: spTier,
    value: spSubscriptionLengthInMonths,
  });

  Analytics.trackGoogleEvent({
    category: 'sign_up_completed_successfully',
    action: gaTier,
    label: gaSubscriptionLength,
    channel: referralKey,
    value,
  });

  // Clear the previous ecommerce object
  Analytics.trackGoogleEvent({ ecommerce: null });

  Analytics.trackGoogleEvent({
    event: 'purchase',
    ecommerce: {
      transaction_id,
      value,
      tax,
      currency,
      coupon,
      items: [
        {
          item_id: tier,
          item_name: `wetransfer ${tier}`,
          coupon,
          currency,
          discount,
          item_variant: interval,
          price: value,
          quantity: 1,
        },
      ],
    },
  });

  const regularAdditionalProperties = {
    'Subscription Tier': getAmplitudeSubscriptionTier(tier),
    'Billing Period': getBillingInterval(spTier, interval),
  };

  const extraAdditionalProperties = {
    'Coupon Code': coupon,
    'Coupon Value': discount,
  };

  const subscriptionTracking = {
    ...regularAdditionalProperties,
    ...(coupon && { ...extraAdditionalProperties }),
  };

  trackAmplitudeEvent({
    action: 'Subscription Created',
    additionalProperties: { ...subscriptionTracking },
  });

  coupon && setAmplitudeUserProperties({ Coupon: coupon });
}

export function paymentFailureOnSignup(
  orderValue: number,
  error: string
): void {
  const referralKey = Analytics.getReferralKey() || '';

  Analytics.trackGoogleEvent({
    category: 'sign_up_payment_failed',
    label: error,
    channel: referralKey,
    value: orderValue,
  });
}

export function paymentFailureOnUpdate(
  orderValue: number,
  error: string
): void {
  Analytics.trackGoogleEvent({
    category: 'update_payment_failed',
    label: error,
    value: orderValue,
  });
}
